import { apiPipelines } from "@/api/recruit/pipeline.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiPipeline, ApiPipelineSavePayload } from "@/types/recruit/pipeline.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

const pipelinesKeys = createQueryKeys("pipelines", {
  all: null,
  details: (id: number) => ({
    queryKey: [id],
    queryFn: async () => {
      const result = await apiPipelines.getById(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const usePipelines = (enabled: boolean = true) => {
  return useQuery({
    ...pipelinesKeys.all,
    queryFn: async () => {
      const result = await apiPipelines.getAll();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 10 * 60 * 1000,
    enabled: enabled,
  });
};

export const usePipelineById = (id: number, enabled: boolean = true) => {
  const queryClient = useQueryClient();
  return useQuery({
    ...pipelinesKeys.details(id),
    initialData: () => {
      const cachedData = queryClient.getQueryData(pipelinesKeys.all.queryKey) as
        ApiPipeline[] | undefined;
      return cachedData?.find((d) => d.id === id);
    },
    staleTime: 10 * 60 * 1000,
    enabled: enabled,
  });
};

export interface SaveParams {
  id: number;
  payload: ApiPipelineSavePayload;
}

export const usePipelineSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ id, payload }: SaveParams) => {
      const result =
        id == 0 ? await apiPipelines.create(payload) : await apiPipelines.update(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: pipelinesKeys.all.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const usePipelineDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiPipelines.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: pipelinesKeys.all.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
